import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, errMsg, ghs } from "../../lib/api";
import { EmptyState, PageHeader, StatusBadge } from "../../components/site/Bits";
import { FullLoader } from "../../components/site/Guards";
import { Field } from "../public/Auth";
import { Modal, Table, Td, Toggle, Uploader } from "./kit";

const BLANK = { name: "", short_description: "", description: "", price: "", category_id: "", image_path: null, is_available: true, delivery_type: "text", delivery_value: "", delivery_file_path: null, delivery_file_name: null };

const ProductForm = ({ initial, cats, onSaved, onClose }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, price: Number(f.price), category_id: f.category_id || null };
    try {
      if (f.id) await api.put(`/admin/products/${f.id}`, body); else await api.post("/admin/products", body);
      toast.success("Product saved"); onSaved();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="product-form">
      <Field label="Product name"><input required minLength={2} value={f.name} onChange={set("name")} className="input-rx" data-testid="product-form-name" /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Price (GH₵)"><input required type="number" min="0.01" step="0.01" value={f.price} onChange={set("price")} className="input-rx" data-testid="product-form-price" /></Field>
        <Field label="Category">
          <select value={f.category_id || ""} onChange={set("category_id")} className="input-rx" data-testid="product-form-category">
            <option value="">Uncategorised</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Short description"><input maxLength={200} value={f.short_description} onChange={set("short_description")} className="input-rx" data-testid="product-form-short" /></Field>
      <Field label="Full description"><textarea rows={4} value={f.description} onChange={set("description")} className="input-rx" data-testid="product-form-description" /></Field>
      <Field label="Product image"><Uploader value={f.image_path} onUploaded={(d) => setF({ ...f, image_path: d.path })} label="Upload image" testId="product-form-image-upload" /></Field>
      <div className="rounded-xl border border-white/[0.06] p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Delivery (shown only to buyers)</p>
        <select value={f.delivery_type} onChange={set("delivery_type")} className="input-rx mb-3" data-testid="product-form-delivery-type">
          <option value="text">Text / instructions / licence key</option><option value="link">Download or access link</option><option value="file">Uploaded file</option>
        </select>
        {f.delivery_type === "file" ? (
          <Uploader kind="file" value={f.delivery_file_name} onUploaded={(d) => setF({ ...f, delivery_file_path: d.path, delivery_file_name: d.file_name })} label="Upload file" testId="product-form-file-upload" />
        ) : (
          <textarea rows={3} value={f.delivery_value} onChange={set("delivery_value")} placeholder={f.delivery_type === "link" ? "https://…" : "Instructions or licence key"} className="input-rx" data-testid="product-form-delivery-value" />
        )}
      </div>
      <Toggle checked={f.is_available} onChange={(v) => setF({ ...f, is_available: v })} label="Available for purchase" testId="product-form-available" />
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        <button disabled={busy} className="btn-primary" data-testid="product-form-save">{busy ? "Saving…" : "Save product"}</button>
      </div>
    </form>
  );
};

const Categories = ({ cats, reload }) => {
  const [name, setName] = useState("");
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/categories", { name }); setName(""); reload(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/categories/${id}`); reload(); } catch (e2) { toast.error(errMsg(e2)); } };
  return (
    <div className="card-rx p-6" data-testid="admin-categories">
      <h2 className="font-semibold text-white">Product categories</h2>
      <div className="mt-4 flex flex-wrap gap-2">
        {cats.length === 0 && <span className="text-sm text-slate-500">No categories yet.</span>}
        {cats.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-sm text-slate-200">
            {c.name}<button onClick={() => del(c.id)} className="text-slate-500 hover:text-red-300" data-testid={`category-delete-${c.slug}`}><X size={13} /></button>
          </span>
        ))}
      </div>
      <form onSubmit={add} className="mt-4 flex gap-2">
        <input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="New category, e.g. E-books" className="input-rx" data-testid="category-name-input" />
        <button className="btn-ghost" data-testid="category-add-btn"><Plus size={15} /> Add</button>
      </form>
    </div>
  );
};

export default function AdminProducts() {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/products").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const catName = Object.fromEntries(cats.map((c) => [c.id, c.name]));

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Existing buyers keep access.`)) return;
    try { await api.delete(`/admin/products/${p.id}`); toast.success("Product deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Admin" title="Products" text="Manage the digital-products catalogue.">
        <button onClick={() => setEdit(BLANK)} className="btn-primary" data-testid="admin-add-product-btn"><Plus size={16} /> Add product</button>
      </PageHeader>
      <Categories cats={cats} reload={load} />
      {items === null ? <FullLoader /> : items.length === 0 ? (
        <EmptyState icon={Package} testId="admin-products-empty" title="No products yet" text="Add your first digital product to start selling." action={<button onClick={() => setEdit(BLANK)} className="btn-primary">Add product</button>} />
      ) : (
        <Table head={["Product", "Category", "Price", "Delivery", "Status", ""]} testId="admin-products-table">
          {items.map((p) => (
            <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
              <Td className="font-medium text-white">{p.name}</Td>
              <Td className="text-slate-400">{catName[p.category_id] || "—"}</Td>
              <Td className="font-mono-rx text-amber-400">{ghs(p.price_minor)}</Td>
              <Td className="capitalize text-slate-400">{p.delivery_type}</Td>
              <Td><StatusBadge status={p.is_available ? "active" : "cancelled"} label={p.is_available ? "Available" : "Hidden"} /></Td>
              <Td className="text-right">
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEdit({ ...BLANK, ...p, price: (p.price_minor / 100).toFixed(2) })} className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white" data-testid={`admin-product-edit-${p.id}`}><Pencil size={15} /></button>
                  <button onClick={() => del(p)} className="rounded-lg p-2 text-slate-400 hover:bg-red-500/10 hover:text-red-300" data-testid={`admin-product-delete-${p.id}`}><Trash2 size={15} /></button>
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? "Edit product" : "Add product"} testId="product-modal">
        {edit && <ProductForm initial={edit} cats={cats} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      </Modal>
    </div>
  );
}
